// src/components/product/ReviewIntegrationProductSelectModal.tsx
import * as React from "react";
import useSWR from "swr";
import { Modal, Input, Typography, Table, Pagination, Space, Spin } from "antd";
import type { ColumnsType } from "antd/es/table";
import { axiosInstance } from "src/api/axiosInstance";
import CustomButton from "src/components/common/CustomButton";

export type SelectedProductItem = {
  productId: number;
  productName: string;
  productCode: string;
};

type Props = {
  open: boolean;
  selectedIds: number[];
  currentProductId?: number; // 현재 상품 ID (선택 불가)
  onClose: () => void;
  onConfirm: (selectedItems: SelectedProductItem[]) => void;
};

interface ProductApiItem {
  productId: number;
  productCode: string;
  productName: string;
}

interface ProductListResponse {
  content: ProductApiItem[];
  page: {
    size: number;
    number: number;
    totalElements: number;
    totalPages: number;
  };
}

const getProductListApi = async (params: {
  productName?: string;
  page: number;
  size: number;
  order: string;
}) => {
  const res = await axiosInstance.get<{ data: ProductListResponse }>(
    "/v1/admin/baseinfo/product/list",
    {
      params,
      snackbar: { success: false },
    }
  );
  return res.data;
};

export default function ReviewIntegrationProductSelectModal({
  open,
  selectedIds,
  currentProductId,
  onClose,
  onConfirm,
}: Props) {
  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [size] = React.useState(10);
  const [tempSelected, setTempSelected] = React.useState<number[]>([]);

  React.useEffect(() => {
    if (open) {
      setTempSelected([...selectedIds]);
      setSearch("");
      setPage(1);
    }
  }, [open, selectedIds]);

  const { data, isLoading } = useSWR(
    open ? ["product/list/review-integration", search, page, size] : null,
    async () => {
      const apiPage = Math.max(0, page - 1);
      return await getProductListApi({
        productName: search.trim() || undefined,
        page: apiPage,
        size,
        order: "id,desc",
      });
    },
    { revalidateOnFocus: false }
  );

  const totalElements = data?.data?.page?.totalElements ?? 0;
  const productList = data?.data?.content ?? [];

  // 현재 페이지에서 선택 가능한 상품들
  const selectableProducts = productList.filter((p) => p.productId !== currentProductId);
  const selectableProductIds = selectableProducts.map((p) => p.productId);

  // 현재 페이지의 선택 가능한 상품들이 모두 선택되었는지 확인
  const isAllSelected =
    selectableProductIds.length > 0 &&
    selectableProductIds.every((id) => tempSelected.includes(id));

  const handleRowSelect = (productId: number) => {
    setTempSelected((prev) => {
      if (prev.includes(productId)) {
        return prev.filter((id) => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      // 현재 페이지의 선택 가능한 상품들 제거
      setTempSelected((prev) => prev.filter((id) => !selectableProductIds.includes(id)));
    } else {
      // 현재 페이지의 선택 가능한 상품들 추가
      setTempSelected((prev) => {
        const newSet = new Set([...prev, ...selectableProductIds]);
        return Array.from(newSet);
      });
    }
  };

  const handleConfirm = () => {
    const selectedProducts: SelectedProductItem[] = productList
      .filter((p) => tempSelected.includes(p.productId))
      .map((p) => ({
        productId: p.productId,
        productName: p.productName,
        productCode: p.productCode,
      }));

    // 이전에 선택된 항목 중 현재 목록에 없는 것들도 유지
    const existingSelected = selectedIds
      .filter((id) => !productList.some((p) => p.productId === id))
      .filter((id) => tempSelected.includes(id));

    onConfirm([
      ...selectedProducts,
      ...existingSelected.map((id) => ({
        productId: id,
        productName: `상품 ID: ${id}`,
        productCode: "",
      })),
    ]);
    onClose();
  };

  const columns: ColumnsType<ProductApiItem> = [
    {
      title: (
        <input
          type="checkbox"
          checked={isAllSelected}
          onChange={handleSelectAll}
          disabled={selectableProducts.length === 0}
          style={{
            width: 18,
            height: 18,
            cursor: selectableProducts.length === 0 ? "not-allowed" : "pointer",
          }}
        />
      ),
      key: "select",
      width: 80,
      align: "center",
      render: (_, record) => {
        const isCurrentProduct = record.productId === currentProductId;
        return (
          <input
            type="checkbox"
            checked={tempSelected.includes(record.productId)}
            onChange={() => handleRowSelect(record.productId)}
            disabled={isCurrentProduct}
            style={{
              width: 18,
              height: 18,
              cursor: isCurrentProduct ? "not-allowed" : "pointer",
            }}
          />
        );
      },
    },
    {
      title: "상품 ID",
      dataIndex: "productId",
      key: "productId",
      width: 100,
    },
    {
      title: "상품 코드",
      dataIndex: "productCode",
      key: "productCode",
      width: 150,
    },
    {
      title: "상품명",
      dataIndex: "productName",
      key: "productName",
      ellipsis: true,
      render: (value: string, record) => (
        <Space>
          <Typography.Text>{value}</Typography.Text>
          {record.productId === currentProductId && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              (현재 상품)
            </Typography.Text>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={900}
      centered
      title={<Typography.Text strong> 상품 선택</Typography.Text>}
      footer={[
        <CustomButton key="cancel" variant="outlined" onClick={onClose}>
          취소
        </CustomButton>,
        <CustomButton key="confirm" variant="contained" onClick={handleConfirm}>
          선택 완료 ({tempSelected.length})
        </CustomButton>,
      ]}
    >
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <Input
          placeholder="상품명으로 검색"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onPressEnter={() => setPage(1)}
          allowClear
        />

        {isLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
            <Spin />
          </div>
        ) : (
          <>
            <Table
              columns={columns}
              dataSource={productList}
              pagination={false}
              rowKey="productId"
              locale={{ emptyText: "상품이 없습니다." }}
              scroll={{ x: 1000, y: 400 }}
            />
            <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
              <Pagination
                current={page}
                total={totalElements}
                pageSize={size}
                showSizeChanger={false}
                onChange={(nextPage) => setPage(nextPage)}
              />
            </div>
          </>
        )}
      </Space>
    </Modal>
  );
}
